import React, { useContext } from 'react'
import StateSelector from '../../design-components/StateSelector'
import ZipCodeTextField from '../../design-components/ZipCodeTextField'
import { Typography, TextField as FoundationsTextField, Icon, Select as FoundationsSelect } from 'foundations-library/components'
import { Grid } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core'
import CreditCardForm from './CreditCardForm'
import LeadContext from '../../LeadContext'

const useStyles = makeStyles(() =>
    createStyles({
        requiredFieldsText: {
            color: '#197F9F !important',
            marginBottom: '15px'
        },
        footerText: {
            marginTop: '10px'
        },
        select: {
            height: '44px'
        },
        lockIcon: {
            width: '18px',
            height: '18px',
            marginBottom: '4px'
        },
        zipHelperText: {
            marginTop: '7px'
        },
        secureCheckoutForm: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '15px',
            alignSelf: 'stretch',
            '& .text-field': {
                border: 0
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
        },
        grouped: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '20px',
            alignSelf: 'stretch'
        },
        foundationsTextField: {
            '& .text-field': {
                border: 0
            }
        },
        fieldContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch'
        },
        helperText: {
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            alignSelf: 'stretch'
        }
    })
)

const fieldLabels = {
    street: 'Billing address',
    city: 'City',
    state: 'State',
    billing_zip_code: 'Postal code', // eslint-disable-line @typescript-eslint/naming-convention
    billing_country_code: 'Country' // eslint-disable-line @typescript-eslint/naming-convention
}

const countryOptions = [
    { value: 'US', text: 'United States' },
    { value: 'CA', text: 'Canada' }
]

const addressFieldName = 'street'
const cityFieldName = 'city'

/**
 * Form to collect business information as part of the sign-up checkout process
 */
const SecureCheckoutForm = ({ skipCreditCardEntry } : { skipCreditCardEntry?: boolean }) : JSX.Element => {
    const classes = useStyles()

    const { leadParams, setLeadParams, checkingZip, isZipValid, savingLead } = useContext(LeadContext)

    const disabled = savingLead

    const renderFoundationsField = (
        field: string,
        fieldType: string,
        onChange?: (value: string) => void | undefined,
        { wrapper, helperText }: { wrapper?: boolean, helperText?: string } = { wrapper: false, helperText: '' }
    ) : JSX.Element => {
        const onFieldChange = (value: string) => {
            if (onChange) {
                onChange(value)
            } else {
                setLeadParams({ ...leadParams, [field]: value })
            }
        }

        const getAutoCompleteAttribute = () => {
            switch (field) {
                    case addressFieldName:
                        return 'billing street-address'
                    case cityFieldName:
                        return 'billing address-level2'
                    default:
                        return undefined
            }
        }

        const autoCompleteAttribute = getAutoCompleteAttribute()

        // console.log('renderFoundationsField', field, fieldType, fieldLabels[field], leadParams[field] || '', onFieldChange, disabled)
        const foundationsField = (
            <FoundationsTextField
                data-testid={field}
                name={field}
                type={fieldType}
                label={fieldLabels[field as keyof typeof fieldLabels]}
                value={leadParams[field as keyof typeof leadParams] as string || ''}
                onChange={onFieldChange}
                disabled={disabled}
                fullWidth
                className={classes.foundationsTextField}
                {...(autoCompleteAttribute ? { autoComplete: autoCompleteAttribute } : {})}
            />
        )

        return (
            wrapper
                ? <div className={classes.fieldContainer}>
                    {foundationsField}
                    <div className={classes.helperText}>
                        <Icon name='notification-alert' size='small' color='neutral-400' />
                        <Typography variant="body4" color="neutral-400">{helperText}</Typography>
                    </div>
                </div>
                : foundationsField
        )
    }
    console.log('leadParams', leadParams)
    return (
        <div className={classes.secureCheckoutForm}>
            <div className={classes.header}>
                <Typography variant='h7' color='neutral-900'>
                    Secure checkout
                </Typography>
                <Icon name='lock' size='small' color='neutral-700' />
            </div>

            <Grid container spacing={3}>
                {!skipCreditCardEntry &&
                    <Grid item xs={12}>
                        <CreditCardForm disabled={disabled} />
                    </Grid>
                }

                <Grid item xs={12} md={12}>
                    {renderFoundationsField(addressFieldName, 'text')}
                </Grid>

                <Grid item xs={12} md={6}>
                    {renderFoundationsField(cityFieldName, 'text')}
                </Grid>

                <Grid item xs={12} md={6}>
                    <StateSelector
                        className={classes.select}
                        data-testid="state"
                        name="state"
                        countryCode={leadParams.billing_country_code || 'US'}
                        value={leadParams.state || ''}
                        onChange={val => {
                            setLeadParams({
                                ...leadParams,
                                state: val
                            })
                        }}
                        disabled={disabled}
                        autoComplete='billing address-level1'
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <ZipCodeTextField
                        name="billing_zip_code"
                        countryCode={leadParams.billing_country_code || 'US'}
                        value={leadParams.billing_zip_code || ''}
                        onChange={val => {
                            setLeadParams({
                                ...leadParams,
                                billing_zip_code: val // eslint-disable-line @typescript-eslint/naming-convention
                            })
                        }}
                        fullWidth={true}
                        disabled={disabled}
                        error={!checkingZip && !isZipValid}
                        autoComplete='billing postal-code'
                    />
                    {checkingZip
                        ? (
                            <Typography variant="helperText" align="left" display="block" className={classes.zipHelperText}>
                            Checking...
                            </Typography>
                        )
                        : !isZipValid
                            ? (
                                <Typography variant="helperText" align="left" display="block" color="#E85646" className={classes.zipHelperText}>
                            This entry is invalid
                                </Typography>
                            )
                            : null}
                </Grid>

                <Grid item xs={12} md={6}>
                    <FoundationsSelect
                        className={classes.select}
                        name="country"
                        value={leadParams.billing_country_code || ''}
                        options={countryOptions}
                        {...{ 'data-testid': 'billing_country_code' }}
                        disabled={disabled}
                        label = 'Country'
                        autoComplete='billing country'
                        onChange={(value) => {
                            const opt = countryOptions.find(opt => opt.value === value)

                            if (!opt) {
                                throw new Error('invalid option')
                            }

                            setLeadParams({
                                ...leadParams,
                                country: opt.text,
                                state: '', // Reset this as user will have to pick again
                                billing_zip_code: '', // eslint-disable-line @typescript-eslint/naming-convention
                                billing_country_code: opt.value // eslint-disable-line @typescript-eslint/naming-convention
                            })
                        }}
                    />
                </Grid>

            </Grid>
        </div>
    )
}

export default SecureCheckoutForm
